/**
 * Heritage role icons (stroke-based, CSS-styled): the gatehouse, paired battle
 * standards, and claimed gatehouse that tell the bidding narrative and persist
 * as trick-slot watermarks. See docs/components/ICONOGRAPHY.md § Role Icons.
 */
import { CEREMONY, NEUTRAL_CLOTH } from "../constants.js";
import { SVG_NS, svg, path, line, filledPath, filledDot } from "./svg.js";

/** True when an identity stroke needs a vellum rim to lift off Midnight Petrol.
 *  Light strokes (human white) already lift on their own; stacking a near-white
 *  rim under a near-white stroke reads as a fuzzy double edge — most visible on
 *  the result-screen hero gatehouse at 132 px. Rec. 601 luminance, threshold 200
 *  cleanly separates the three IDENTITY_COLOR options (white 255 vs dark slate
 *  ~60 vs imperial grey ~84). Exported as the family's shared rim gate —
 *  outcome-icons.js applies the same contract to the ruin. */
export function needsRim(strokeColor) {
  if (!strokeColor) return false;
  const h = strokeColor.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b < 200;
}

/** Appends the gatehouse outer wall (battlement with 3 merlons) and gate arch to `parent`.
 *  Shared by `createGatehouse` and `createGatehouseClaimed`. When `strokeColor` is set,
 *  applies it inline — identity coloring rides inline styles so no host CSS can
 *  override it. The claimed variant uses this for full
 *  identity coloring (DESIGN.md § Player Identity); the open variant leaves it undefined
 *  so neutral hardware draws `currentColor` and recolors with the host.
 *
 *  When the identity stroke is dark, also prepends a vellum-stroked underlay at a
 *  wider stroke-width — same halo trick as the cloth rim, extended to the gatehouse
 *  structure so dark identity strokes lift off Midnight Petrol. Light strokes skip
 *  the rim (see `needsRim`).
 *
 *  Exported (with the node refs returned) as the geometry seam for the outcome
 *  board beat (animation/outcome-board-beat.js): the beat composes the factory's
 *  own builders into its animation groups, so its start frame IS the factory
 *  rendering — and the returned refs are its morph targets (a dark identity's
 *  rims must morph in lockstep with their colored twins). */
export function appendGatehouseWalls(parent, strokeColor) {
  const wallD =
    "M8 38 L8 20 L14 20 L14 12 L22 12 L22 20 L26 20 L26 12 L34 12 L34 20 L40 20 L40 38";
  const archD = "M20 38 L20 30 Q24 26 28 30 L28 38";
  let wallRim = null;
  let archRim = null;
  if (needsRim(strokeColor)) {
    wallRim = path(wallD, 4);
    wallRim.style.stroke = "#E2E8F0";
    parent.appendChild(wallRim);
    archRim = path(archD, 3.5);
    archRim.style.stroke = "#E2E8F0";
    parent.appendChild(archRim);
  }
  const wall = path(wallD, 2.5);
  const arch = path(archD, 2);
  if (strokeColor) {
    wall.style.stroke = strokeColor;
    arch.style.stroke = strokeColor;
  }
  parent.appendChild(wall);
  parent.appendChild(arch);
  return { wall, arch, wallRim, archRim };
}

/** Appends the 4 portcullis bars (2 horizontal, 2 vertical) inside the gate opening.
 *  Shared by the claimed-variant gatehouses, the animated `createPortcullisBars` group,
 *  and the outcome board beat (which passes its own <g> so the bars drop as one).
 *  See `appendGatehouseWalls` for why `strokeColor` is applied inline and for the rim. */
export function appendPortcullis(parent, strokeColor) {
  const coords = [
    [20, 32, 28, 32],
    [20, 35, 28, 35],
    [23, 28, 23, 38],
    [25, 28, 25, 38],
  ];
  if (needsRim(strokeColor)) {
    for (const [x1, y1, x2, y2] of coords) {
      const rim = line(x1, y1, x2, y2, 2.5);
      rim.style.stroke = "#E2E8F0";
      parent.appendChild(rim);
    }
  }
  for (const [x1, y1, x2, y2] of coords) {
    const bar = line(x1, y1, x2, y2, 1.5);
    if (strokeColor) bar.style.stroke = strokeColor;
    parent.appendChild(bar);
  }
}

/** Appends a swallowtail banner cloth with identity fill and a vellum rim to `parent`.
 *  Fill and stroke set via inline style — identity coloring always rides inline styles
 *  so no host CSS can override it (DESIGN.md § Player Identity).
 *  The vellum rim separates dark opponent cloths from the petrol stage and gives the human's
 *  pure-white cloth a visible frame-vs-surface distinction. Exported as the identity-cloth
 *  contract shared with the outcome standards (outcome-icons.js) — cloth carries identity,
 *  hardware stays neutral. */
export function addSwallowtailCloth(parent, pathD, clothColor, rimWidth) {
  const cloth = filledPath(pathD);
  cloth.style.fill = clothColor;
  cloth.style.stroke = "#E2E8F0";
  cloth.setAttribute("stroke-width", String(rimWidth));
  cloth.setAttribute("stroke-linejoin", "round");
  parent.appendChild(cloth);
  return cloth; // the board beat's morph target — fill + rim ride the one node
}

export function createGatehouse(size, variant = "open") {
  const s = svg(size, size);
  appendGatehouseWalls(s);
  if (variant === "claimed") appendPortcullis(s);
  return s;
}

/**
 * Create portcullis bar overlay for gatehouse SVG animation.
 * Returns a <g> element with horizontal and vertical bars, pre-positioned
 * above the gate opening and ready for drop animation.
 * @returns {SVGGElement}
 */
export function createPortcullisBars() {
  const bars = document.createElementNS(SVG_NS, "g");
  bars.setAttribute("class", "portcullis-bars");
  bars.style.transformBox = "fill-box";
  bars.style.transform = "translateY(-15px)";
  bars.style.opacity = "0";
  appendPortcullis(bars);
  return bars;
}

/** The battle standard's pole-base pivot as a transform-origin percentage of its
 *  box. why: the pole base sits at (24, 46) in the "0 -2 48 52" viewBox below →
 *  x = 24/48 = 50%, y = (46 − (−2))/52 ≈ 92.31%; the .role-icon div bbox equals
 *  the svg bbox (skat.css pins this as load-bearing), so the percentage holds on
 *  a wrapping div at any icon size. Pivots here — the dramatic fall
 *  (banner-topple.js) and the bidding-stage rebuild that mirrors its rest
 *  (bidding-ui). Resting slot/chip watermarks pivot at their own centre instead,
 *  so the cloth's mass stays centred (ICONOGRAPHY § The Standard Family). */
export const POLE_BASE_ORIGIN = "50% 92.31%";

/** Appends one battle standard's pole, finial, crossbar, and cloth (+ folds) to `parent` —
 *  the geometry `createBattleStandard` wraps in its own `<svg>`, factored out so
 *  `createBattleStandardPair` can compose two copies into one shared viewBox instead of
 *  nesting two independent `<svg>` elements. */
function appendBattleStandardParts(parent, clothColor) {
  // Pole — centered vertical
  parent.appendChild(line(24, 46, 24, 5, 2.5));
  // Ornate finial: orb + cross at top of pole
  parent.appendChild(filledDot(24, 3, 2));
  parent.appendChild(line(24, -1, 24, 1, 1.5)); // cross vertical
  parent.appendChild(line(22.8, 0, 25.2, 0, 1.5)); // cross horizontal
  // Crossbar with finial caps
  parent.appendChild(line(10, 7, 38, 7, 2));
  parent.appendChild(line(10, 5.5, 10, 8.5, 1.5)); // left cap
  parent.appendChild(line(38, 5.5, 38, 8.5, 1.5)); // right cap
  // Cloth + folds ride one group so the banner-topple fall can swing the cloth
  // about the crossbar independently of the rigid pole/hardware — paint-neutral
  // for static renders (the appendClaimedBanner grouping precedent).
  const clothGroup = document.createElementNS(SVG_NS, "g");
  clothGroup.setAttribute("class", "banner-cloth");
  addSwallowtailCloth(
    clothGroup,
    "M10 7 L10 35 L24 29 L38 35 L38 7 Z",
    clothColor,
    1.2,
  );
  // Curved fold lines on cloth (Q bezier for natural drape). Creases are shadows,
  // so the stroke is always darker than the cloth — inline style, like all
  // identity-bound coloring, so no host CSS can recolor it.
  const fold = (x, cx) => {
    const f = document.createElementNS(SVG_NS, "path");
    f.setAttribute("d", `M${x} 8 Q${cx} 20 ${x - 0.5} 33`);
    f.setAttribute("fill", "none");
    f.style.stroke = "#1a1a2e";
    f.setAttribute("stroke-width", "0.8");
    f.setAttribute("opacity", "0.2");
    return f;
  };
  clothGroup.appendChild(fold(18, 16));
  clothGroup.appendChild(fold(30, 32));
  parent.appendChild(clothGroup);
}

/** Battle standard — single standing banner with swallowtail cloth and ornate finial.
 *  `clothColor` drives player identity (see IDENTITY_COLOR); defaults to NEUTRAL_CLOTH
 *  for contexts without a specific player (settings, result side watermarks). */
export function createBattleStandard(size, clothColor = NEUTRAL_CLOTH) {
  const s = svg(size, size, "0 -2 48 52");
  appendBattleStandardParts(s, clothColor);
  return s;
}

// The defenders' allied pair (stats-v2 panel redesign design § grammar 5: "the allied pair of
// standards, two, ~45% overlap" — muster vocabulary: declarer storms alone, the defenders ally;
// replaces the gatehouse as the defender-role mark, § Rejected). Overlap fraction of the standard's
// own cloth width (28 units, x:10-38 in createBattleStandard's viewBox) — open per the design's
// "shape-live, not blocker" note, tuned here as a first pixel pass.
const PAIR_OVERLAP = 0.45; // play-test: how much the two cloths overlap
const PAIR_LEAN_DEG = 5; // the one lean, both pairs
const PAIR_HALF_OFFSET = ((1 - PAIR_OVERLAP) * 28) / 2;

// The separated pair's half-offset (the /games row/chip/KPI mark): CEREMONY.DEFENDER_LEAN of the
// standard's own viewBox width (48 — createBattleStandard draws in "0 -2 48 52"), so the constant's
// own "a fraction of icon size" why stays literally true however large the pair renders.
const SEPARATED_HALF_OFFSET = CEREMONY.DEFENDER_LEAN * 48;
// Measured, not derived: a getBBox() probe on the composed pair in Chromium (2026-08-29)
// read x −4.60…52.60, y −2.04…47.22 — the two translated standards plus the ±5° lean about the
// pole base — rounded outward to whole units. Re-measure whenever an input to that geometry moves:
// CEREMONY.DEFENDER_LEAN (shared with the bidding ceremony's own spread, so a change made for the
// ceremony lands here too) or PAIR_LEAN_DEG. The box IS the painted footprint, and the e2e fence
// (game-history-smoke.spec.js#expectPairPaintsInsideItsBox) measures precisely that — the painted
// banners against this box — so a stale viewBox reddens instead of quietly letting paint escape.
const SEPARATED_VIEWBOX = "-5 -3 58 51";

// Both pairs are the same composition — two standards translated apart and leaned outward about
// the shared pole base — differing only in how far apart they sit, what cloth each wears, and how
// wide a box that footprint needs. The bottom-pivot rotate(24 46) is POLE_BASE_ORIGIN in user
// units: a banner leans from where its pole meets the ground, never about its middle.
function composeStandardPair(size, viewBox, halfOffset, cloths) {
  const s = svg(size, size, viewBox);
  [
    [-halfOffset, -PAIR_LEAN_DEG],
    [halfOffset, PAIR_LEAN_DEG],
  ].forEach(([dx, deg], i) => {
    const g = document.createElementNS(SVG_NS, "g");
    g.setAttribute("transform", `translate(${dx} 0) rotate(${deg} 24 46)`);
    appendBattleStandardParts(g, cloths[i]);
    s.appendChild(g);
  });
  return s;
}

/** The defenders' allied pair — two `createBattleStandard` renders composed into one shared
 *  viewBox, leaned outward from the pole base (`POLE_BASE_ORIGIN`'s pivot) with their cloths
 *  overlapping ~45%. `clothColor` applies uniformly to both (never per-side identity — every
 *  stats-v2 panel call site is a role marker: matrix axis, cell mark, Null defense tokens, not a
 *  specific player), defaulting to `NEUTRAL_CLOTH` like `createBattleStandard`. The cloth fill is
 *  an inline style (the identity-cloth contract, `addSwallowtailCloth`'s own Javadoc) — it beats
 *  CSS `color` inheritance, so a caller wanting the matrix axis markers' gold accent
 *  (design § grammar 4) must pass it explicitly here rather than relying on a host color rule. */
export function createBattleStandardPair(size, clothColor = NEUTRAL_CLOTH) {
  return composeStandardPair(size, "-14 -6 76 58", PAIR_HALF_OFFSET, [
    clothColor,
    clothColor,
  ]);
}

/** The defenders' separated pair — the same composition spread far enough apart to read as two
 *  distinct banners (`CEREMONY.DEFENDER_LEAN`) rather than one allied mass, and optionally
 *  two-coloured: `cloths` is the pair's cloth colours in banner order (left, right), which the
 *  /games round row fills with the two defenders' identities so you can read who you partnered
 *  with. The chip rail and the KPI card pass nothing — there the pair is a role emblem, not two
 *  specific players — and both banners draw the neutral vellum.
 *
 *  Its viewBox is wider than tall, so a `size × size` host `<svg>` letterboxes the composite and
 *  the banners paint at ~0.85 of the slot's height rather than filling it. That is intended
 *  (games-filters-ux-pass design § 2), not a shrink to correct: the box being the painted
 *  footprint is what keeps the leaned pair inside whatever slot holds it. */
export function createSeparatedStandardPair(
  size,
  cloths = [NEUTRAL_CLOTH, NEUTRAL_CLOTH],
) {
  return composeStandardPair(
    size,
    SEPARATED_VIEWBOX,
    SEPARATED_HALF_OFFSET,
    cloths,
  );
}

/** Gatehouse with banner planted on right battlement — Alleinspieler trick slot watermark.
 *  `identityColor` colors the gatehouse structure (walls, arch, portcullis) and the mini-
 *  banner cloth — the claimed gatehouse carries the declarer's identity throughout
 *  (DESIGN.md § Player Identity). Banner pole + finial + crossbar stay vellum (frame=vellum
 *  rule). Omitted for non-player-specific contexts: walls draw `currentColor`
 *  (vellum under `.role-icon.neutral`), cloth defaults
 *  to NEUTRAL_CLOTH — the icon renders entirely vellum. */
/** The claimed gatehouse's banner hardware — pole on the right battlement step
 *  plus a hardware <g> (finial orb + cross, crossbar with caps). All vellum
 *  (`currentColor`) per the frame=vellum rule; the cloth alone carries identity.
 *  Exported for the outcome board beat, which appends into its own pole group
 *  so the loss topple rotates pole + hardware as one (the beat fades the
 *  hardware group separately mid-fall). The grouping is paint-neutral for the
 *  static factory render. Pole base sits at (37, 20) — the topple's pivot. */
export function appendClaimedBanner(parent) {
  const pole = line(37, 20, 37, -4, 2);
  parent.appendChild(pole);
  const hardware = document.createElementNS(SVG_NS, "g");
  // Mini finial: orb + cross
  hardware.appendChild(filledDot(37, -5.5, 1.5));
  hardware.appendChild(line(37, -8.5, 37, -7, 1.2));
  hardware.appendChild(line(36, -7.8, 38, -7.8, 1.2));
  // Mini crossbar with caps
  hardware.appendChild(line(31, -3, 43, -3, 1.5));
  hardware.appendChild(line(31, -4.2, 31, -1.8, 1.2));
  hardware.appendChild(line(43, -4.2, 43, -1.8, 1.2));
  parent.appendChild(hardware);
  return { pole, hardware };
}

/** The hanging banner cloth on the claimed gatehouse — also the board beat's
 *  morph source (its win stir returns here; the loss drape morphs away from it). */
export const CLAIMED_CLOTH_D = "M31 -3 L31 13 L37 10 L43 13 L43 -3 Z";

/** The claimed gatehouse with its animation-relevant node refs returned —
 *  the resolution reveal docks the flying gatehouse onto the measured `wall`
 *  rect and the winner's banner onto the measured `cloth` rect, then snaps
 *  the cloth at the plant. The composite the player rests on IS this factory
 *  output; the refs just keep the choreography measured, never hand-synced. */
export function createGatehouseClaimedParts(size, identityColor) {
  const s = svg(size, size, "0 -10 48 58");
  const { wall } = appendGatehouseWalls(s, identityColor);
  appendPortcullis(s, identityColor);
  appendClaimedBanner(s);
  const cloth = addSwallowtailCloth(
    s,
    CLAIMED_CLOTH_D,
    identityColor ?? NEUTRAL_CLOTH,
    0.8,
  );
  return { svgEl: s, wall, cloth };
}

export function createGatehouseClaimed(size, identityColor) {
  return createGatehouseClaimedParts(size, identityColor).svgEl;
}
